import { todayISO, fmtShort } from "@/lib/api";

const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;

export function StageAge({ lead, className = "" }) {
  const s = lead.age_status;
  if (s === "closed") return <span className={`text-xs text-gray-400 ${className}`}>Closed</span>;
  const styles = {
    overdue: "bg-red-50 text-red-700 ring-red-200",
    approaching: "bg-amber-50 text-amber-700 ring-amber-200",
    normal: "bg-gray-100 text-gray-600 ring-gray-200",
  };
  const label = s === "overdue" ? `Overdue by ${plural(lead.overdue_by, "day")}`
    : s === "approaching" ? `${plural(lead.days_left, "day")} left to act`
      : `${plural(lead.days_in_stage, "day")} in stage`;
  return (
    <span data-testid={`stage-age-${lead.lead_id}`} title={`${plural(lead.days_in_stage, "day")} in current stage · last action ${plural(lead.idle_days, "day")} ago`}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${styles[s]} ${className}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s === "overdue" ? "bg-red-500" : s === "approaching" ? "bg-amber-500" : "bg-gray-400"}`} />
      {label}
    </span>
  );
}

export function FollowUp({ date }) {
  if (!date) return <span className="text-gray-400">—</span>;
  const t = todayISO();
  if (date === t) return <span className="text-amber-700 font-medium">Due today</span>;
  if (date < t) return <span className="text-red-600 font-medium">Overdue · {fmtShort(date)}</span>;
  return <span className="text-gray-700">{fmtShort(date)}</span>;
}

export function StagePill({ name, outcome }) {
  const c = outcome === "won" ? "bg-[#ECF5F1] text-[#0B3C2D]" : outcome === "lost" ? "bg-gray-100 text-gray-500" : "bg-white text-gray-700 ring-1 ring-inset ring-gray-200";
  return <span className={`inline-flex rounded-md px-2 py-0.5 text-[11.5px] font-medium whitespace-nowrap ${c}`}>{name}</span>;
}

export function PriorityDot({ p }) {
  const c = { High: "bg-[#0B3C2D]", Medium: "bg-emerald-400", Low: "bg-gray-300" }[p] || "bg-gray-200";
  return <span className="inline-flex items-center gap-1.5 text-xs text-gray-600"><span className={`h-2 w-2 rounded-full ${c}`} />{p || "—"}</span>;
}

export function Empty({ icon: Icon, title, text, action }) {
  return (
    <div className="text-center py-12 px-6" data-testid="empty-state">
      {Icon && <div className="mx-auto h-10 w-10 rounded-full bg-[#ECF5F1] grid place-items-center mb-3"><Icon className="h-5 w-5 text-[#0B3C2D]" /></div>}
      <p className="text-sm font-medium text-gray-900">{title}</p>
      {text && <p className="text-sm text-gray-500 mt-1">{text}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
    <div>
      <h1 className="font-display text-2xl font-bold tracking-tight text-gray-900" data-testid="page-title">{title}</h1>
      {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
    </div>
    {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
  </div>
);

export const Skeletons = ({ n = 4 }) => (
  <div className="space-y-2" data-testid="loading-skeleton">
    {Array.from({ length: n }).map((_, i) => <div key={i} className="h-12 rounded-lg bg-gray-100 animate-pulse" />)}
  </div>
);
